"use client";
import React, { useState } from "react";
import Image from "next/image";
import { toast } from "sonner";
import FloatingSelect from "@/components/ui/FloatingSelect";
import Button from "../ui/Button";
import axiosInstance, { endpoints } from "@/lib/axios";
import { useRouter } from "next/navigation";

// Contact Page replicating the provided design and matching input/select/textarea styles
const ContactForm: React.FC = () => {
  const router = useRouter();
  const [form, setForm] = useState({
    company: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneCode: "+33",
    phone: "",
    topic: "",
    message: "",
    // "" = pas encore répondu, "oui" = prise de rdv, "non" = pas de prise de rdv
    wantsAppointment: "" as "" | "oui" | "non",
    appointmentDate: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const topics = [
    { value: "strategy", label: "Stratégie d'entreprise" },
    { value: "marketing", label: "Marketing et contenus" },
    { value: "social", label: "Réseaux sociaux" },
    { value: "productivity", label: "Outils et productivité" },
    { value: "coaching", label: "Coaching / Accompagnement" },
    { value: "other", label: "Autre" },
  ];

  const inputClass =
    "w-full px-3 py-2.5 bg-white border border-[#EFEFEF] rounded-lg text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE] h-10";
  const textareaClass =
    "w-full bg-white border border-[#EFEFEF] rounded-lg px-3 py-2.5 text-[#1D1D1D] text-xs leading-5 tracking-[2%] placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE] h-[132px]";

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target as HTMLInputElement;
      setForm((s) => ({ ...s, [name]: value }));
    };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.topic || !form.message) {
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const wantsAppointment = form.wantsAppointment === "oui";
      await axiosInstance.post(endpoints.leads.create, {
        first_name: form.firstName,
        last_name: form.lastName,
        company: form.company || undefined,
        email: form.email,
        phone: `${form.phoneCode}${form.phone}`,
        formation_theme: topics.find((t) => t.value === form.topic)?.label,
        message: form.message,
        origin: "contact",
        wants_appointment: wantsAppointment,
        appointment_date: wantsAppointment && form.appointmentDate ? form.appointmentDate : undefined,
      });
      toast.success("Demande envoyée avec succès.");
      setForm({ company: "", firstName: "", lastName: "", email: "", phoneCode: "+33", phone: "", topic: "", message: "", wantsAppointment: "", appointmentDate: "" });
    } catch {
      setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
      toast.error("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const isDisabled =
    submitting ||
    !form.firstName ||
    !form.lastName ||
    !form.email ||
    !form.phone ||
    !form.topic ||
    !form.message;

  return (    
    <section className="px-4 md:px-6 pb-16 md:pt-[70px] pt-8">
    {/* Back */}
    <div className="relative mb-5">
      <Button variant="outline-gradient" onClick={() => router.back()} className="h-[36px] w-[100px] md:w-[121px] rounded-[8px] md:text-[15px] text-[14px]! absolute left-0 md:top-0 -top-10 group">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path className="fill-[#FC7F16] group-hover:fill-white" fillRule="evenodd" clipRule="evenodd" d="M15 8C15 8.47339 14.6082 8.85714 14.125 8.85714L3.98744 8.85714L7.74372 12.5368C8.08543 12.8715 8.08543 13.4142 7.74372 13.7489C7.40201 14.0837 6.84799 14.0837 6.50628 13.7489L1.25628 8.60609C0.914573 8.27136 0.914573 7.72864 1.25628 7.39391L6.50628 2.25105C6.84799 1.91632 7.40201 1.91632 7.74372 2.25105C8.08543 2.58579 8.08543 3.1285 7.74372 3.46323L3.98744 7.14286L14.125 7.14286C14.6082 7.14286 15 7.52661 15 8Z" fill="#FC7F16"/>
        </svg>
        Retour
      </Button>
    </div>

    {/* Title */}
    <div className="text-center max-w-[567px] mx-auto">
        <h1 className="text-3xl sm:text-[40px] font-red-hat font-light tracking-[2%] text-[#1B365E]">
        Un besoin ou une question ?
        </h1>
        <p className="mt-7 md:mt-10 text-[#1B365E] text-[20px] font-medium tracking-[2%]">
        Complétez ce formulaire, un membre de notre équipe vous contactera très bientôt
        </p>
    </div>

    {/* Form */}
    <form onSubmit={onSubmit} className="mx-auto max-w-[412px] mt-7">
        {/* Company (optional) */}
        <div className="space-y-2 w-full">
          <label htmlFor="company" className="sr-only">
              Entreprise
          </label>
          <input
              id="company"
              name="company"
              value={form.company}
              onChange={onChange}
              placeholder="Entreprise (facultatif)"
              className={inputClass}
          />
        </div>

        {/* First name */}
        <div className="md:mt-7.5 mt-5 space-y-2 w-full">
          <label htmlFor="firstName" className="sr-only">
              Prénom
          </label>
          <input
              id="firstName"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              placeholder="Prénom"
              className={inputClass}
          />
        </div>

        {/* Last name */}
        <div className="md:mt-7.5 mt-5 space-y-2 w-full">
          <label htmlFor="lastName" className="sr-only">
              Nom
          </label>
          <input
              id="lastName"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              placeholder="Nom"
              className={inputClass}
          />
        </div>

        {/* Email */}
        <div className="md:mt-7.5 mt-5 space-y-2 w-full">
          <label htmlFor="email" className="sr-only">
              Email
          </label>
          <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              placeholder="Email"
              className={inputClass}
          />
        </div>

        {/* Phone */}
        <div className="md:mt-7.5 mt-5 flex gap-4.5">
          <div className="w-[50px]">
              <label htmlFor="phoneCode" className="sr-only">
              Indicatif
              </label>
              <input
              id="phoneCode"
              name="phoneCode"
              value={form.phoneCode}
              onChange={onChange}
              placeholder="+33"
              className={inputClass}
              />
          </div>
          <div className="flex-1">
              <label htmlFor="phone" className="sr-only">
              Portable
              </label>
              <input
              id="phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="Portable"
              className={inputClass}
              />
          </div>
        </div>

        {/* Topic (Select) */}
        <div className="md:mt-7.5 mt-5">
        <FloatingSelect
            label="Quel sujet répond le mieux à vos besoins ?"
            name="topic"
            value={form.topic}
            onChange={(e) => setForm((s) => ({ ...s, topic: e.target.value }))}
            options={topics}
            placeholder="Choisissez une option"
            lessGap={true}
        />
        </div>

        {/* Message */}
        <div className="md:mt-7.5 mt-5 space-y-2 w-full">
        <p className="text-[#1B365E] font-medium">Que pouvons-nous faire pour vous ?</p>
        <label htmlFor="message" className="sr-only">
            Message
        </label>
        <textarea
            id="message"
            name="message"
            value={form.message}
            onChange={onChange}
            placeholder="J’ai besoin d’être accompagné sur la création de ma stratégie d’entreprise. Je voudrais aussi construire un plan marketing et trouver des clients."
            rows={4}
            className={textareaClass}
        />
        </div>

        {/* Book appointment */}
        <div className="md:mt-7.5 mt-5">
          <p className="text-[#1B365E]">
            Souhaitez-vous prendre rendez-vous avec un conseiller ?
          </p>
          <div role="radiogroup" aria-label="Souhaitez-vous prendre rendez-vous avec un conseiller ?" className="mt-2 flex gap-3">
            {([
              { value: "oui", label: "Oui" },
              { value: "non", label: "Non" },
            ] as const).map((choice) => {
              const isChecked = form.wantsAppointment === choice.value;
              return (
                <button
                  key={choice.value}
                  type="button"
                  role="radio"
                  aria-checked={isChecked}
                  onClick={() =>
                    setForm((s) => ({
                      ...s,
                      wantsAppointment: choice.value,
                      appointmentDate: choice.value === "oui" ? s.appointmentDate : "",
                    }))
                  }
                  className={`inline-flex min-w-26 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                    isChecked
                      ? "border-[#40BFDE] bg-[#40BFDE]/10 font-medium text-[#40BFDE]"
                      : "border-[#EFEFEF] bg-white text-[#1D1D1D] hover:border-[#40BFDE]"
                  }`}
                >
                  {choice.value === "oui" && (
                    <span className="flex h-6 w-[21px] shrink-0 items-center justify-center">
                      <Image src="/assets/icons/calendar-plus.svg" width={21} height={24} alt="" />
                    </span>
                  )}
                  {choice.label}
                </button>
              );
            })}
          </div>
          {form.wantsAppointment === "oui" && (
            <input
              type="datetime-local"
              name="appointmentDate"
              value={form.appointmentDate}
              onChange={onChange}
              onClick={(event) => {
                try {
                  event.currentTarget.showPicker?.();
                } catch {
                  /* no picker available — the field still types/taps normally */
                }
              }}
              className="mt-3 block w-full min-w-0 max-w-full appearance-none px-3 py-2.5 bg-white border border-[#40BFDE] rounded-lg text-[#1D1D1D] text-xs focus:outline-none focus:ring-0 h-10"
            />
          )}
        </div>

        {/* Submit */}
        <div className="md:mt-7.5 mt-5">
          {error && <p className="mb-3 text-xs text-red-600">{error}</p>}
          <Button
            type="submit"
            disabled={isDisabled}
            className={`w-full px-4 py-4 transition-colors ${
              isDisabled
                  ? "bg-[#D9D9D9]! text-[#ABABAB]! cursor-not-allowed border border-[#ABABAB]"
                  : "bg-[#1B365E] text-white hover:bg-[#1B365E]/90! hover:text-white border border-[#1B365E]"
              }`}
          >
            {isDisabled ? "Envoyer ma demande" : submitting ? "Envoi..." : "Envoyer ma demande"}
          </Button>
          <p className="md:mt-7.5 mt-5 text-xs text-[#1B365E]">
            *Tous les champs sont obligatoires, sauf « Entreprise »
          </p>
        </div>
    </form>
    </section>
  );
};

export default ContactForm;
